import React from "react";
import { motion } from "framer-motion";
import {
  Clock,
  Landmark,
  Coins,
  ShieldCheck,
  Trophy,
  Users,
  UserPlus,
  Headphones,
} from "lucide-react";

/* ================= DATA ================= */
const cards = [
  {
    icon: Clock,
    title: "Learn Anytime, 24x7",
    desc:
      "Learn2Earn24x7 gives you complete flexibility to learn anytime, anywhere. " +
      "There are no fixed classes or time restrictions — you can study at your own pace, whenever it suits you.",
  },
  {
    icon: Landmark,
    title: "Covers All Government Exams",
    desc:
      "Prepare for UPSC, SSC, Banking, Railway, State PSCs, and more — all in one place. " +
      "Everything you need is available under one roof.",
  },
  {
    icon: Coins,
    title: "Earn Coins & Cash",
    desc:
      "With Learn2Earn24x7, learning is not just about preparation. " +
      "Every correct answer helps you earn coins, which can be converted into real cash rewards.",
  },
  {
    icon: ShieldCheck,
    title: "Transparent Reward System",
    desc:
      "Our reward system is fully transparent and rule-based. " +
      "There are no hidden conditions or luck-based outcomes. " +
      "Your earnings depend only on your performance and skills.",
  },
  {
    icon: Trophy,
    title: "Engaging Quizzes & Contests",
    desc:
      "Participate in daily quizzes, weekly challenges, and competitive contests designed to match real exam patterns.",
  },
  {
    icon: Users,
    title: "Trusted by Students",
    desc:
      "Learn2Earn24x7 is designed with a student-first approach. Thousands of aspirants trust our platform for fair rewards.",
  },
  {
    icon: UserPlus,
    title: "Growing Learning Community",
    desc:
      "Join a growing community of students from across the country. Healthy competition keeps you motivated.",
  },
  {
    icon: Headphones,
    title: "24x7 Support",
    desc:
      "Our dedicated support team is available 24x7 to assist you with quizzes, contests, wallet, or account-related queries.",
  },
];

/* ================= ANIMATIONS ================= */
const containerVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12 } },
};

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: "easeOut" },
  },
};

/* ================= COMPONENT ================= */
const WhyChooseUs = () => {
  return (
    <section className="w-full py-12 bg-White">
      {/* Heading */}
      <div className="text-center mb-10">
        <h2 className="text-3xl md:text-4xl font-bold text-black">
          Why Choose <span className="text-purple">Learn2Earn24x7</span>
        </h2>
        <p className="mt-3 text-black/80 max-w-2xl mx-auto text-sm">
          Empowering aspirants to learn, earn & grow with transparency.
        </p>
      </div>

      {/* Cards */}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        className="max-w-6xl mx-auto px-6 grid grid-cols-1 md:grid-cols-2 gap-6"
      >
        {cards.map((item, index) => {
          const Icon = item.icon;

          return (
            <motion.div
              key={index}
              variants={cardVariants}
              whileHover={{ y: -4 }}
              className="
                flex items-center gap-5
                bg-bgBlue backdrop-blur-md
                border border-purple
                rounded-2xl
                px-6 py-5
                text-white
              "
            >
              {/* Icon */}
              <div
                className="
                  w-[56px] h-[56px]
                  flex-shrink-0
                  flex items-center justify-center
                  rounded-xl
                  border border-purple
                  text-purple
                  bg-bgBlue/10
                "
              >
                <Icon size={26} />
              </div>

            
              <div className="flex-1 text-left text-purple ">
                <h3 className="text-[16px] font-semibold mb-1">
                  {item.title}
                </h3>

                <p className="text-white/85 text-[13.5px] leading-relaxed">
                  {item.desc}
                </p>
              </div>
            </motion.div>
          );
        })}
      </motion.div>
    </section>
  );
};

export default WhyChooseUs;
